import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ToggleButton, mergeClasses, tokens } from '@fluentui/react-components';
import { HorizontalBarChartWithAxis } from '@fluentui/react-charting';
import type { IHorizontalBarChartWithAxisDataPoint } from '@fluentui/react-charting';
import { WidgetContainer } from '../WidgetContainer';
import { ChartThemeProvider } from '../ChartThemeProvider';
import type { RoomDetail } from '@/types/reports';
import { useRoomUtilizationRankingStyles } from './RoomUtilizationRanking.styles';

export interface RoomUtilizationRankingProps {
  data: RoomDetail[];
  limit?: number;
}

export const RoomUtilizationRanking = ({ data, limit = 10 }: RoomUtilizationRankingProps) => {
  const styles = useRoomUtilizationRankingStyles();
  const { t } = useTranslation('reports');
  const [mode, setMode] = useState<'top' | 'bottom'>('top');

  const chartData: IHorizontalBarChartWithAxisDataPoint[] = useMemo(() => {
    const sorted = [...data].sort((a, b) => b.utilization - a.utilization);
    const sliced = mode === 'top' ? sorted.slice(0, limit) : sorted.slice(-limit).reverse();
    return sliced.map((room) => ({
      x: room.utilization,
      y: room.name,
      color: room.utilization >= 80
        ? tokens.colorPaletteRedForeground1
        : tokens.colorPaletteGreenForeground1,
    }));
  }, [data, mode, limit]);

  return (
    <WidgetContainer title={t('widgets.roomRankingTitle')}>
      <div className={styles.root} role="figure" aria-label={t('widgets.roomRankingTitle')}>
        <div className={styles.toggleRow}>
          <ToggleButton
            className={mergeClasses(styles.toggleButton, styles.toggleButtonFirst)}
            checked={mode === 'top'}
            onClick={() => setMode('top')}
            appearance={mode === 'top' ? 'primary' : 'outline'}
            size="small"
          >
            {t('widgets.top10')}
          </ToggleButton>
          <ToggleButton
            className={mergeClasses(styles.toggleButton, styles.toggleButtonLast)}
            checked={mode === 'bottom'}
            onClick={() => setMode('bottom')}
            appearance={mode === 'bottom' ? 'primary' : 'outline'}
            size="small"
          >
            {t('widgets.bottom10')}
          </ToggleButton>
        </div>
        <ChartThemeProvider>
          <HorizontalBarChartWithAxis
            data={chartData}
            showYAxisLables
            yAxisTitle=""
            height={350}
            enableReflow={true}
            hideLegend
          />
        </ChartThemeProvider>
      </div>
    </WidgetContainer>
  );
};

RoomUtilizationRanking.displayName = 'RoomUtilizationRanking';
